import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/Key';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppBadIcon from '@mui/icons-material/GppBad';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../contexts/AuthContext';
import { createApiClient } from '../services/apiClient';

// PERMISSIONS moved inside for i18n

const PermCheck = ({ value }) =>
  value ? (
    <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
  ) : (
    <BlockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
  );

// ROLE_LABELS definition moved inside component for i18n support

const UserManagement = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', username: '', password: '', confirmPassword: '', name: '', role: 'read-only' });
  const [createLoading, setCreateLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const PERMISSIONS = useMemo(() => [
    { label: t('View patients & records'), admin: true, 'read-write': true, 'read-only': true },
    { label: t('Add new patients'), admin: true, 'read-write': true, 'read-only': false },
    { label: t('Edit patient data'), admin: true, 'read-write': true, 'read-only': false },
    { label: t('Delete patients'), admin: true, 'read-write': false, 'read-only': false },
    { label: t('Add visits / medications / operations'), admin: true, 'read-write': true, 'read-only': false },
    { label: t('Delete visits / medications / operations'), admin: true, 'read-write': false, 'read-only': false },
    { label: t('Access Settings & User Management'), admin: true, 'read-write': false, 'read-only': false },
    { label: t('Create / delete users'), admin: true, 'read-write': false, 'read-only': false },
  ], [t]);
  const ROLE_LABELS = useMemo(() => ({
    admin: { label: t('Administrator'), color: 'error', description: t('Full access — read, write, delete, manage users') },
    'read-write': { label: t('User'), color: 'warning', description: t('Can add & edit patients, cannot delete') },
    'read-only': { label: t('Viewer'), color: 'default', description: t('Read-only access to all patient data') },
  }), [t]);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', username: '', role: '', password: '', confirmPassword: '' });
  const [editLoading, setEditLoading] = useState(false);

  const apiClient = useMemo(() => {
    const token = sessionStorage.getItem('token');
    return token ? createApiClient(token) : null;
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!apiClient) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/auth/users');
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser, fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    if (!apiClient) return;
    setUpdatingUserId(userId);
    setError('');
    setSuccess('');
    try {
      const res = await apiClient.put(`/auth/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: res.data.user.role } : u));
      setSuccess('Role updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleStatusToggle = async (userItem) => {
    if (!apiClient) return;
    const newStatus = userItem.status === 'active' ? 'inactive' : 'active';
    setUpdatingUserId(userItem.id);
    setError('');
    setSuccess('');
    try {
      const res = await apiClient.put(`/auth/users/${userItem.id}/status`, { status: newStatus });
      setUsers(prev => prev.map(u => u.id === userItem.id ? { ...u, status: res.data.user.status } : u));
      setSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handle2FAToggle = async (userItem) => {
    if (!apiClient) return;
    const newEnabled = !userItem.two_factor_enabled;
    setUpdatingUserId(userItem.id);
    setError('');
    setSuccess('');
    try {
      const res = await apiClient.put(`/auth/users/${userItem.id}/2fa`, { enabled: newEnabled });
      setUsers(prev => prev.map(u => u.id === userItem.id ? { ...u, two_factor_enabled: res.data.user.two_factor_enabled } : u));
      setSuccess(`2FA ${newEnabled ? 'enabled' : 'disabled'} for ${userItem.username || userItem.email}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update 2FA status');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleDelete = async () => {
    if (!apiClient || !deleteTarget) return;
    try {
      await apiClient.delete(`/auth/users/${deleteTarget.id}`);
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      setSuccess('User deleted successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCreateUser = async () => {
    if (!apiClient) return;
    if (!createForm.email || !createForm.username || !createForm.password) {
      setError('Email, username, and password are required');
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setCreateLoading(true);
    setError('');
    try {
      const res = await apiClient.post('/auth/users', createForm);
      setUsers(prev => [...prev, res.data.user]);
      setSuccess(`User ${createForm.username} created successfully`);
      setCreateForm({ email: '', username: '', password: '', name: '', role: 'read-only' });
      setShowCreateForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditOpen = (userItem) => {
    setEditTarget(userItem);
    setEditForm({
      name: userItem.name || '',
      email: userItem.email || '',
      username: userItem.username || '',
      role: userItem.role || 'read-only',
      password: '', // Keep password empty by default
      confirmPassword: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError('');
    setSuccess('');
  };

  const handleUpdateUser = async () => {
    if (!apiClient || !editTarget) return;
    if (!editForm.email || !editForm.username) {
      setError('Email and username are required');
      return;
    }
    if (editForm.password && editForm.password !== editForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setEditLoading(true);
    setError('');
    try {
      const res = await apiClient.put(`/auth/users/${editTarget.id}`, editForm);
      setUsers(prev => prev.map(u => u.id === editTarget.id ? res.data.user : u));
      setSuccess(`User ${editForm.username} updated successfully`);
      setEditTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  // Only show for admins
  if (currentUser?.role !== 'admin') return null;

  return (
    <Card
      elevation={0}
      sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              User Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowMatrix(v => !v)}
            >
              {showMatrix ? 'Hide' : 'Show'} Permission Matrix
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowCreateForm(v => !v)}
            >
              Add User
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only administrators can manage users. You can update your own details here, but role and status changes for your own account are restricted for security.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Permission Matrix */}
        {showMatrix && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Role Permission Matrix
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                    <TableCell align="center">
                      <Chip label="Administrator" color="error" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label="User" color="warning" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label="Viewer" size="small" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PERMISSIONS.map((perm) => (
                    <TableRow key={perm.label}>
                      <TableCell>{perm.label}</TableCell>
                      <TableCell align="center"><PermCheck value={perm.admin} /></TableCell>
                      <TableCell align="center"><PermCheck value={perm['read-write']} /></TableCell>
                      <TableCell align="center"><PermCheck value={perm['read-only']} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider sx={{ mt: 2, mb: 2 }} />
          </Box>
        )}

        {/* Create User Form */}
        {showCreateForm && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              {t('Create New User')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Email"
                type="email"
                size="small"
                value={createForm.email}
                onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                label="Username"
                size="small"
                value={createForm.username}
                onChange={e => setCreateForm(p => ({ ...p, username: e.target.value }))}
                sx={{ flex: '1 1 140px' }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                size="small"
                value={createForm.password}
                onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                sx={{ flex: '1 1 140px' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                size="small"
                value={createForm.confirmPassword}
                onChange={e => setCreateForm(p => ({ ...p, confirmPassword: e.target.value }))}
                sx={{ flex: '1 1 140px' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Name (optional)"
                size="small"
                value={createForm.name}
                onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                sx={{ flex: '1 1 140px' }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={createForm.role}
                  onChange={e => setCreateForm(p => ({ ...p, role: e.target.value }))}
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="read-write">User</MenuItem>
                  <MenuItem value="read-only">Viewer (Read-only)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleCreateUser}
                disabled={createLoading}
              >
                {createLoading ? <CircularProgress size={16} /> : t('Create User')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Users Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Identifier</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>2FA</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((userItem) => {
                  const isSelf = String(userItem.id) === String(currentUser?.id);
                  const roleInfo = ROLE_LABELS[userItem.role] || ROLE_LABELS['read-only'];
                  const isUpdating = updatingUserId === userItem.id;

                  return (
                    <TableRow
                      key={userItem.id}
                      sx={{ opacity: userItem.status === 'inactive' ? 0.55 : 1 }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            @{userItem.username || 'no-username'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {userItem.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {userItem.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isSelf ? (
                          <Chip label={roleInfo.label} color={roleInfo.color} size="small" variant="outlined" />
                        ) : (
                          <FormControl size="small" disabled={isUpdating || isSelf}>
                            <Select
                              value={userItem.role}
                              onChange={e => handleRoleChange(userItem.id, e.target.value)}
                              variant="standard"
                              sx={{ minWidth: 120, fontSize: '0.8rem' }}
                            >
                              <MenuItem value="admin">Administrator</MenuItem>
                              <MenuItem value="read-write">User</MenuItem>
                              <MenuItem value="read-only">Viewer (Read-only)</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {userItem.two_factor_enabled ? (
                            <VerifiedUserIcon fontSize="inherit" color="success" />
                          ) : (
                            <GppBadIcon fontSize="inherit" color="action" />
                          )}
                          <Typography variant="caption" color={userItem.two_factor_enabled ? 'success.main' : 'text.secondary'}>
                            {userItem.two_factor_enabled ? 'ON' : 'OFF'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={userItem.status === 'active' ? 'Active' : 'Locked'}
                          color={userItem.status === 'active' ? 'success' : 'error'}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatLastLogin(userItem.last_login)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                          <Tooltip title="Edit user details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditOpen(userItem)}
                              disabled={isUpdating}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {!isSelf && (
                            <>
                              <Tooltip title={userItem.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={userItem.two_factor_enabled ? 'success' : 'default'}
                                    onClick={() => handle2FAToggle(userItem)}
                                    disabled={isUpdating}
                                  >
                                    <KeyIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={userItem.status === 'active' ? 'Lock Account' : 'Unlock Account'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={userItem.status === 'active' ? 'warning' : 'success'}
                                    onClick={() => handleStatusToggle(userItem)}
                                    disabled={isUpdating}
                                  >
                                    {userItem.status === 'active' ? (
                                      <BlockIcon fontSize="small" />
                                    ) : (
                                      <CheckCircleIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete user permanently">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteTarget(userItem)}
                                    disabled={isUpdating}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {isSelf && (
                            <Chip label="You" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteTarget?.name || deleteTarget?.email}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog 
        open={Boolean(editTarget)} 
        onClose={() => !editLoading && setEditTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          {t('Edit User')}: {editTarget?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={editForm.name}
              onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={editForm.email}
                onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
              />
              <TextField
                label="Username"
                fullWidth
                value={editForm.username}
                onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))}
              />
            </Box>
            <FormControl fullWidth disabled={String(editTarget?.id) === String(currentUser?.id)}>
              <InputLabel>{t('Role')}</InputLabel>
              <Select
                label={t('Role')}
                value={editForm.role}
                onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
              >
                <MenuItem value="admin">{t('Administrator')}</MenuItem>
                <MenuItem value="read-write">{t('User')}</MenuItem>
                <MenuItem value="read-only">{t('Viewer')}</MenuItem>
              </Select>
            </FormControl>
            <Divider>
              <Typography variant="caption" color="text.secondary">Security</Typography>
            </Divider>
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              placeholder="Leave blank to keep current password"
              value={editForm.password}
              onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))}
              helperText="Only fill this if you want to reset the user's password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {editForm.password && (
              <TextField
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={editForm.confirmPassword}
                onChange={e => setEditForm(p => ({ ...p, confirmPassword: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setEditTarget(null)} disabled={editLoading}>Cancel</Button>
          <Button 
            onClick={handleUpdateUser} 
            variant="contained" 
            disabled={editLoading}
            sx={{ px: 4 }}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default UserManagement;
